import { FileText, Home, Search, X } from "lucide-react";
import { CopyPageUrlButton } from "@/components/copy-page-url-button";
import { ShieldCogIcon } from "@/components/shield-cog-icon";
import { LongLabelTooltip } from "@/components/ui/long-label-tooltip";
import { Tabs } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTabs } from "@/contexts/tabs-context";
import { useCommandPalette } from "@/features/command-palette";
import { NotificationBell } from "@/features/notifications";
import { cn } from "@/lib/utils";

export function AppHeader() {
  const { tabs, activeTabId, isHome, setActiveTab, closeTab, goHome } = useTabs();
  const { openPalette, shortcutLabel } = useCommandPalette();

  const hasTabs = tabs.length > 0;

  return (
    <header className="hidden md:flex flex-col bg-background">
      <div className="flex h-12 items-center gap-2 px-6">
        {hasTabs && (
          <Tabs
            value={isHome ? "home" : (activeTabId ?? "")}
            onValueChange={(value) => {
              if (value === "home") goHome();
              else setActiveTab(value as string);
            }}
            className="min-w-0 flex-1"
          >
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-none" role="tablist">
              {/* Home icon — permanent, not closable */}
              <button
                type="button"
                role="tab"
                aria-selected={isHome}
                onClick={goHome}
                className={cn(
                  "flex h-8 shrink-0 items-center justify-center px-2 transition-colors",
                  isHome ? "bg-muted" : "text-neutral-dim hover:text-foreground"
                )}
              >
                <Home className="size-4" />
              </button>

              {/* Vertical divider */}
              <div className="h-5 w-px shrink-0 bg-border" />

              {/* Project tabs with close buttons */}
              {tabs.map((tab) => {
                const isActive = activeTabId === tab.tabId;
                return (
                  <div
                    key={tab.tabId}
                    className={cn(
                      "group flex h-8 shrink-0 items-center gap-1.5 px-2.5 transition-colors",
                      isActive ? "bg-muted" : "hover:bg-muted/50"
                    )}
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(tab.tabId)}
                      className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                    >
                      {tab.kind === "admin" ? (
                        <ShieldCogIcon
                          className={cn(
                            "size-3.5 shrink-0",
                            isActive ? "text-primary" : "text-neutral-dim"
                          )}
                        />
                      ) : (
                        <FileText
                          className={cn(
                            "size-3.5 shrink-0",
                            isActive ? "text-primary" : "text-neutral-dim"
                          )}
                        />
                      )}
                      <LongLabelTooltip
                        content={tab.name}
                        side="bottom"
                        contentClassName="max-w-80 whitespace-normal break-words"
                      >
                        <span
                          className={cn(
                            "max-w-[192px] truncate text-[13px]",
                            isActive ? "font-medium text-foreground" : "text-muted-foreground"
                          )}
                        >
                          {tab.name}
                        </span>
                      </LongLabelTooltip>
                    </button>
                    <button
                      type="button"
                      aria-label={`Close ${tab.name} tab`}
                      className={cn(
                        "p-0.5 transition-opacity hover:bg-muted",
                        "opacity-100 no-touch:opacity-0 no-touch:group-hover:opacity-100 focus-visible:opacity-100",
                        isActive && "opacity-100"
                      )}
                      onClick={() => {
                        void closeTab(tab.tabId);
                      }}
                    >
                      <X className="size-3 text-neutral-dim" />
                    </button>
                  </div>
                );
              })}
            </div>
          </Tabs>
        )}

        {/* Spacer when no tabs */}
        {!hasTabs && <div className="flex-1" />}

        {/* Right side: Search + Notification bell + Copy link */}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <Tooltip>
            <TooltipTrigger
              className="flex size-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
              aria-label={`Search (${shortcutLabel})`}
              onClick={openPalette}
            >
              <Search className="size-4" />
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <span>Search ({shortcutLabel})</span>
            </TooltipContent>
          </Tooltip>
          <NotificationBell />
          <CopyPageUrlButton />
        </div>
      </div>
      <div className="h-px bg-border" />
    </header>
  );
}
